import React from 'react'
import Image from 'next/image'
import { Box, useResponsiveValue } from '@machobear/design-system'

import { useCarousel, type CarouselImageData } from '../../hooks/useCarousel'
import { Arrows } from './Arrows'
import { Dots } from './Dots'
import * as styles from './style.css.ts'

export interface CarouselProps {
  imageUrls: string[]
  showNav?: boolean
  children?: (url: string) => React.ReactNode
  priority?: boolean
  alt?: string
}

export const Carousel: React.FC<CarouselProps> = ({
  imageUrls,
  children,
  priority = false,
  alt = 'Listing image'
}) => {
  const {
    currentIndex,
    displayIndex,
    isTransitioning,
    imageData,
    slideWidth,
    totalSlides,
    showNav,
    goNext,
    goPrev,
    setIndex,
    swipeHandlers
  } = useCarousel(imageUrls)

  const isMobile = useResponsiveValue()({ mobile: true, tablet: false })

  if (imageUrls.length === 0) {
    return null
  }

  const renderImage = (data: CarouselImageData, index: number) => {
    const { url, originalIndex, isCurrentImage } = data

    return (
      <Box
        key={`${url}-${index}`}
        className={styles.slide}
        style={{ width: `${slideWidth}%` }}
        role="group"
        aria-roledescription="slide"
        aria-label={`${originalIndex + 1} of ${imageUrls.length}`}
      >
        {children ? (
          children(url)
        ) : (
          <Image
            src={url}
            alt={`${alt} ${originalIndex + 1} of ${imageUrls.length}`}
            fill
            style={{ objectFit: 'cover' }}
            priority={priority && isCurrentImage}
            loading={isCurrentImage ? 'eager' : 'lazy'}
            quality={85}
          />
        )}
      </Box>
    )
  }

  return (
    <Box
      className={styles.carousel}
      {...swipeHandlers}
      style={{ touchAction: 'pan-y' }}
      role="region"
      aria-label="Image carousel"
      aria-live="polite"
      aria-atomic="false"
    >
      <Box
        className={styles.slideTrack}
        style={{
          transform: `translateX(-${displayIndex * slideWidth}%)`,
          width: `${totalSlides * 100}%`,
          transition: isTransitioning
            ? 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            : 'none'
        }}
        role="group"
        aria-label={`Image ${currentIndex + 1} of ${imageUrls.length}`}
      >
        {imageData.map((data, index) => renderImage(data, index))}
      </Box>

      {showNav && (
        <Box className={isMobile ? null : styles.arrowsContainer}>
          <Arrows onPrev={goPrev} onNext={goNext} />
        </Box>
      )}

      {showNav && (
        <Dots
          count={imageUrls.length}
          activeIndex={currentIndex}
          onDotClick={setIndex}
        />
      )}
    </Box>
  )
}
